import { useCallback, useEffect, useState } from 'react'
import {
  apiGetUserSavedPlaces,
  apiAddUserSavedPlace,
  apiRemoveUserSavedPlace,
} from '../api/profile/userProfileApi'

export type SavedPlaceSource = 'private-tour' | 'day-trip'

export type SavedPlaceItem = {
  id: string
  source: SavedPlaceSource
  title: string
  duration: string
  price: string
  image: string
  explorePath: string
  subtitle?: string
  description?: string
  tags?: readonly string[]
}

const STORAGE_KEY = 'salzburg_saved_places'
const LEGACY_KEY = 'salzburg_saved_private_tours'
const CHANGE_EVENT = 'salzburg-saved-places-changed'

const isSavedPlaceItem = (value: unknown): value is SavedPlaceItem => {
  if (!value || typeof value !== 'object') return false
  const item = value as Record<string, unknown>
  return (
    typeof item.id === 'string' &&
    (item.source === 'private-tour' || item.source === 'day-trip') &&
    typeof item.title === 'string' &&
    typeof item.duration === 'string' &&
    typeof item.price === 'string' &&
    typeof item.image === 'string' &&
    typeof item.explorePath === 'string'
  )
}

const readSavedPlaces = (): SavedPlaceItem[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as unknown
      if (Array.isArray(parsed)) return parsed.filter(isSavedPlaceItem)
    }

    // One-time bridge from the old ID-only private-tour bookmarks.
    const legacy = localStorage.getItem(LEGACY_KEY)
    if (!legacy) return []

    const parsedLegacy = JSON.parse(legacy) as unknown
    if (!Array.isArray(parsedLegacy)) return []

    return parsedLegacy
      .map((id) => String(id))
      .filter(Boolean)
      .map((id) => ({
        id,
        source: 'private-tour' as const,
        title: '',
        duration: '',
        price: '',
        image: '',
        explorePath: `/private-tours?id=${id}`,
      }))
  } catch {
    return []
  }
}

const writeSavedPlaces = (items: SavedPlaceItem[]) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  window.dispatchEvent(new Event(CHANGE_EVENT))
}

/**
 * Persists wishlist cards (day trips + private van tours) so the Saved Places
 * page stays in sync with heart/bookmark toggles across the site.
 */
export default function useSavedPlaces() {
  const [savedPlaces, setSavedPlaces] = useState<SavedPlaceItem[]>(() =>
    typeof window === 'undefined' ? [] : readSavedPlaces(),
  )

  useEffect(() => {
    let mounted = true
    const sync = () => setSavedPlaces(readSavedPlaces())

    window.addEventListener('storage', sync)
    window.addEventListener(CHANGE_EVENT, sync)

    // Sync from server if user is logged in
    const token = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null
    if (token) {
      apiGetUserSavedPlaces().then((res) => {
        if (mounted && res.success && Array.isArray(res.data) && res.data.length > 0) {
          const local = readSavedPlaces()
          const combined = [...local]
          res.data.forEach((item) => {
            if (!combined.some((p) => p.id === item.id)) {
              combined.push(item)
            }
          })
          writeSavedPlaces(combined)
          setSavedPlaces(combined)
        }
      }).catch(() => null)
    }

    return () => {
      mounted = false
      window.removeEventListener('storage', sync)
      window.removeEventListener(CHANGE_EVENT, sync)
    }
  }, [])

  const persist = useCallback((next: SavedPlaceItem[]) => {
    setSavedPlaces(next)
    writeSavedPlaces(next)
  }, [])

  const isSaved = useCallback(
    (id: string | number) => savedPlaces.some((place) => place.id === String(id)),
    [savedPlaces],
  )

  const toggleSaved = useCallback(
    (item: SavedPlaceItem) => {
      const key = String(item.id)
      const isCurrentlySaved = savedPlaces.some((place) => place.id === key)
      if (isCurrentlySaved) {
        apiRemoveUserSavedPlace(key).catch(() => null)
        persist(savedPlaces.filter((place) => place.id !== key))
      } else {
        const normalized = { ...item, id: key }
        apiAddUserSavedPlace(normalized).catch(() => null)
        persist([...savedPlaces, normalized])
      }
    },
    [persist, savedPlaces],
  )

  const removeSaved = useCallback(
    (id: string | number) => {
      const key = String(id)
      apiRemoveUserSavedPlace(key).catch(() => null)
      persist(savedPlaces.filter((place) => place.id !== key))
    },
    [persist, savedPlaces],
  )

  const upsertMany = useCallback(
    (items: SavedPlaceItem[]) => {
      if (items.length === 0) return
      const next = [...savedPlaces]
      items.forEach((item) => {
        const key = String(item.id)
        const index = next.findIndex((place) => place.id === key)
        const normalized = { ...item, id: key }
        if (index === -1) next.push(normalized)
        else next[index] = normalized
      })
      persist(next)
    },
    [persist, savedPlaces],
  )

  return {
    savedPlaces,
    savedIds: savedPlaces.map((place) => place.id),
    isSaved,
    toggleSaved,
    removeSaved,
    upsertMany,
  }
}
